import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AllSportsHubsLogo from './pages/ASH Logo (1).png';
import AdSenseScript from './components/AdSenseScript';

const SportsLandingPage = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('home');

  const showView = (view) => {
    setCurrentView(view);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderHome = () => (
    <>
      <div className="main-content">
        <div className="content-left animate-fade-in-up">
          <h1 className="main-title">Sports scheduling, simplified!</h1>

          <p className="description">
            Organize your team schedules, track games, and manage opponents all in one place.
            Simple, efficient, and designed for sports teams of all levels.
          </p>

          <button
            className="register-button"
            onClick={() => navigate('/register')}
          >
            Register now
          </button>

          <div className="hero-proof" aria-label="All Sports Hubs scheduling highlights">
            <div className="proof-item">
              <strong>Round-robin schedules</strong>
              <span>Create fair rotations across teams without rebuilding matchups by hand.</span>
            </div>
            <div className="proof-item">
              <strong>Venue planning</strong>
              <span>Save grounds and connect game locations to the schedules you create.</span>
            </div>
            <div className="proof-item">
              <strong>Easy updates</strong>
              <span>Edit teams, dates, times, and venues when real season plans change.</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bottom-line"></div>
    </>
  );

  const renderAbout = () => (
    <div className="about-content animate-fade-in-up">
      <div className="about-container">
        <h1 className="about-title">About All Sports Hub</h1>

        <div className="about-description">
          <h2>What We Do</h2>
          <p>
            All Sports Hub is a comprehensive sports league management platform designed to
            simplify the complex task of organizing and managing sports games.
          </p>

          <h2 className="features-heading">Key Features</h2>
          <div className="features-grid">
            <div className="feature-item">
              <h3>Schedule/Ground Management</h3>
              <p>Create, edit, and organize schedules and grounds in one place.</p>
            </div>

            <div className="feature-item feature-coming-soon">
              <h3>Game Tracking</h3>
              <p>Track wins, losses, player statistics, and team performance over time.</p>
              <span className="coming-soon-badge">Coming Soon</span>
            </div>

            <div className="feature-item feature-coming-soon">
              <h3>Opponent Management</h3>
              <p>Maintain opposing teams, strategies, and historical matchups.</p>
              <span className="coming-soon-badge">Coming Soon</span>
            </div>
          </div>

          <h2>Who It's For</h2>
          <p>
            All Sports Hub is mainly used for leagues. It can scale from small local teams
            to larger professional ones. We understand sports scheduling involves long hours
            of organizing games for leagues and tournaments, so we make round-robin schedule
            generation and venue management straightforward.
          </p>
        </div>

        <div className="about-cta">
          <button className="register-button" onClick={() => navigate('/register')}>
            Get Started Today
          </button>

          <button className="back-button" onClick={() => showView('home')}>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );

  const renderSchedulingGuide = () => (
    <div className="about-content animate-fade-in-up">
      <div className="about-container scheduling-guide-card">
        <h1 className="about-title">Sports Scheduling Guide</h1>

        <div className="about-description">
          <h2>How to Create a Cleaner Round-Robin Schedule</h2>
          <p>
            A good sports schedule is more than a list of games. It balances teams,
            venue availability, weekly workload, travel expectations, and the changes that
            happen once a season begins.
          </p>

          <div className="guide-content-grid">
            <div className="guide-block">
              <h3>Start With the Season Structure</h3>
              <p>
                Write down the number of teams, games per team, and weeks available before
                creating matchups. These numbers shape the rest of the schedule.
              </p>
            </div>

            <div className="guide-block">
              <h3>Use Round-Robin Logic</h3>
              <p>
                Round-robin scheduling rotates opponents so teams do not repeatedly face
                the same side while missing others.
              </p>
            </div>

            <div className="guide-block">
              <h3>Plan Venue Capacity Early</h3>
              <p>
                A league may have enough teams and weeks, but not enough ground slots.
                Add venue details early so the schedule can actually be played.
              </p>
            </div>

            <div className="guide-block">
              <h3>Leave Room for Edits</h3>
              <p>
                Weather, field availability, team conflicts, and tournament changes can
                all require updates. A useful schedule should be easy to adjust.
              </p>
            </div>
          </div>

          <h2>Checklist Before Sharing</h2>
          <div className="guide-checklist">
            <div>
              <strong>Teams</strong>
              <span>Names are readable and spelled consistently.</span>
            </div>
            <div>
              <strong>Games</strong>
              <span>Each team has the expected number of games.</span>
            </div>
            <div>
              <strong>Venues</strong>
              <span>Ground assignments and addresses are correct.</span>
            </div>
            <div>
              <strong>Dates</strong>
              <span>Game dates and times match venue availability.</span>
            </div>
          </div>
        </div>

        <div className="about-cta">
          <button className="register-button" onClick={() => navigate('/register')}>
            Start Building a Schedule
          </button>

          <button className="back-button" onClick={() => showView('home')}>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="landing-container">
      <AdSenseScript />
      <nav className="landing-nav">
        <div
          className="logo-section"
          onClick={() => showView('home')}
          style={{ cursor: 'pointer' }}
        >
          <img
            src={AllSportsHubsLogo}
            alt="All Sports Hubs Logo"
            className="logo-icon"
            style={{ height: '125px', width: 'auto', objectFit: 'contain' }}
          />
        </div>

        <div className="nav-links">
          <button
            className={`nav-link nav-link-button ${currentView === 'about' ? 'active' : ''}`}
            onClick={() => showView('about')}
          >
            About Us
          </button>
          <button
            className={`nav-link nav-link-button ${currentView === 'guide' ? 'active' : ''}`}
            onClick={() => showView('guide')}
          >
            Scheduling Guide
          </button>
          <Link className="nav-link" to="/login">Login</Link>
          <button className="nav-button" onClick={() => navigate('/register')}>
            Start for free
          </button>
        </div>
      </nav>

      {currentView === 'home' && renderHome()}
      {currentView === 'about' && renderAbout()}
      {currentView === 'guide' && renderSchedulingGuide()}
    </div>
  );
};

export default SportsLandingPage;
